import { useState } from "react";

/**
 * One hybrid reading, paired with the product it describes.
 *
 * The pairing is supplied by the DETECTOR, not read from the route at write
 * time — see {@link useHybridDetection}.
 */
export interface HybridMeasurement {
  productId: string;
  isHybrid: boolean;
}

/**
 * Holds what `HybridDetector` reported, scoped to the product it was measured
 * on (ET-216).
 *
 * The scoping is the point. The detector can only report from INSIDE the
 * product `<Await>`, so on a PDP→PDP soft nav — where the new product is a
 * promise for the whole navigation and the eager tier is therefore `null` by
 * construction — an unscoped value would keep answering for the PREVIOUS
 * product until the new detector's effect fires. Leaving a non-orderable
 * product for a resolvable one would paint `HybridPDPSkeleton` across the
 * entire streaming window while the normal layout stayed suppressed, costing
 * the nav its optimistic tile-seed hero (SFCC-11255).
 *
 * Discarded in render rather than cleared in an effect: an effect would run
 * AFTER the render that already painted the wrong layout. Scoped rather than
 * cleared, too — the route component is reused across PDP navigations, so
 * returning to an already-measured product answers from that measurement
 * instead of flashing a skeleton while it re-measures.
 *
 * Unseeded (`null`, never the URL param) so the server frame can't disagree
 * with the resolved product, and written by exactly one caller — the detector's
 * `onHybridChange`. A second writer used to fight it and win (a child's effect
 * flushes before its parent's), pinning the value forever.
 *
 * The setter is `useState`'s own, so its identity is STABLE, and the product a
 * reading belongs to travels with the reading rather than being read from this
 * closure. Both halves matter, and a first cut of this hook got it wrong in a
 * way that silently reinstated the bug it was written to fix: with the setter
 * wrapped in a `useCallback` keyed on `productId`, flipping products changed
 * the setter's identity, which is in the detector's effect deps, which re-fired
 * the still-mounted detector, which wrote the OLD product's reading under the
 * NEW product's id. Every scoping test still passed.
 *
 * A stable setter cannot close over the current `productId` either — at effect
 * time that is already the product being navigated TO, not the one measured.
 * Hence the detector supplies it.
 */
export function useHybridDetection(
  productId: string,
): [boolean | null, (measurement: HybridMeasurement) => void] {
  const [detection, setDetection] = useState<HybridMeasurement | null>(null);

  return [
    detection?.productId === productId ? detection.isHybrid : null,
    setDetection,
  ];
}
